/**
 * StepathonRewardsPage — `/stepathon/rewards`
 *
 * Patient-facing hub for the 1-step-=-1-point ecosystem:
 *   • Hero card with earned / spent / available points balance
 *   • Reward catalogue grid (Super-Admin-curated coupons + free refills + merch)
 *   • One-click Redeem with branded confirm prompt
 *   • Redemption history with coupon codes
 *
 * Auth required — anonymous visitors get bounced to /login with returnTo set.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";
import { Gift, Ticket, ShoppingBag, Sparkles, Copy, CheckCircle2 } from "lucide-react";
import SquadCard from "@/components/SquadCard";

const TYPE_META = {
  coupon:      { icon: Ticket,       label: "Coupon" },
  free_refill: { icon: Sparkles,     label: "Refill credit" },
  merch:       { icon: ShoppingBag,  label: "Merch" },
};

function PointsHero({ summary }) {
  return (
    <div className="rounded-3xl border border-purple-300/30 bg-gradient-to-br from-purple-900/70 via-fuchsia-900/40 to-pink-900/40 p-8 shadow-xl">
      <div className="text-xs uppercase tracking-widest text-pink-300 font-bold">
        Your GLPFlo step-points
      </div>
      <div className="mt-3 text-6xl sm:text-7xl font-black tabular-nums bg-gradient-to-r from-yellow-200 via-pink-200 to-fuchsia-200 bg-clip-text text-transparent"
           data-testid="points-available">
        {summary.points_available.toLocaleString()}
      </div>
      <div className="mt-1 text-purple-100/80 text-sm">points available to redeem</div>
      <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-2xl bg-white/5 border border-purple-200/20 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wider text-purple-200/80">Lifetime earned</div>
          <div className="font-bold tabular-nums" data-testid="points-earned">
            {summary.points_earned.toLocaleString()}
          </div>
        </div>
        <div className="rounded-2xl bg-white/5 border border-purple-200/20 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wider text-purple-200/80">Spent</div>
          <div className="font-bold tabular-nums" data-testid="points-spent">
            {summary.points_spent.toLocaleString()}
          </div>
        </div>
      </div>
      <p className="mt-5 text-[12px] text-purple-100/70">
        1 step = 1 point · earn by logging steps on any active Step-a-Thon event ·
        spend on coupons, free refills, and limited-edition merch curated by GLPFlo HQ.
      </p>
    </div>
  );
}

function CouponPill({ code }) {
  const copy = () => {
    navigator.clipboard.writeText(code).then(
      () => toast.success(`Copied ${code} to clipboard`),
      () => toast.error("Could not copy")
    );
  };
  return (
    <button
      onClick={copy}
      data-testid={`copy-coupon-${code}`}
      className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-300/40 px-2 py-1 text-xs font-mono text-emerald-100 transition"
      title="Click to copy"
    >
      <Copy className="w-3 h-3" /> {code}
    </button>
  );
}

function RewardCard({ reward, available, onRedeem }) {
  const Meta = TYPE_META[reward.reward_type] || TYPE_META.coupon;
  const Icon = Meta.icon;
  const canAfford = available >= reward.points_cost;
  const outOfStock = reward.stock > 0 && reward.redeemed_count >= reward.stock;
  const disabled = !canAfford || outOfStock;
  const autoApplies = reward.reward_type !== "merch" && (reward.amount_dollars || 0) > 0;

  return (
    <div
      data-testid={`reward-card-${reward.id}`}
      className={`rounded-3xl border bg-white/5 p-5 transition flex flex-col ${
        disabled ? "border-purple-200/20 opacity-70" : "border-purple-300/30 hover:border-pink-400/60"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-pink-300 font-bold">
          <Icon className="w-3.5 h-3.5" /> {Meta.label}
        </div>
        {outOfStock && (
          <span className="text-[10px] uppercase tracking-widest text-rose-300 font-bold">
            Out of stock
          </span>
        )}
      </div>
      {reward.image_url && (
        <img src={reward.image_url} alt={reward.name}
             className="mt-3 w-full h-32 rounded-2xl object-cover" />
      )}
      <h3 className="mt-3 text-lg font-extrabold">{reward.name}</h3>
      {reward.amount_dollars > 0 && (
        <div className="mt-1 text-2xl font-black text-emerald-300 tabular-nums">
          ${reward.amount_dollars.toFixed(2)} value
        </div>
      )}
      {reward.description && (
        <p className="mt-1 text-sm text-purple-100/80 flex-1">{reward.description}</p>
      )}
      {autoApplies && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
          <Sparkles className="w-3 h-3" /> Auto-applies at checkout
        </div>
      )}
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="tabular-nums">
          <div className="text-2xl font-black bg-gradient-to-r from-yellow-200 to-pink-200 bg-clip-text text-transparent">
            {reward.points_cost.toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-purple-200/70">points</div>
        </div>
        <Button
          data-testid={`redeem-${reward.id}`}
          onClick={() => onRedeem(reward)}
          disabled={disabled}
          className="bg-pink-600 hover:bg-pink-700 text-white disabled:bg-slate-600/50 disabled:text-slate-300"
        >
          {!canAfford ? `Need ${(reward.points_cost - available).toLocaleString()} more` : "Redeem"}
        </Button>
      </div>
    </div>
  );
}

export default function StepathonRewardsPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [badges, setBadges] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [lbWindow, setLbWindow] = useState("all");

  const loadLeaderboard = useCallback(async (win) => {
    try {
      const r = await API.get(`/stepathon/leaderboard?window=${win}`);
      setLeaderboard(r.data);
    } catch {
      setLeaderboard(null);
    }
  }, []);

  useEffect(() => { loadLeaderboard(lbWindow); }, [lbWindow, loadLeaderboard]);

  const load = useCallback(async () => {
    try {
      const [meR, rewardsR, badgesR] = await Promise.all([
        API.get("/stepathon/rewards/me"),
        API.get("/stepathon/rewards"),
        API.get("/stepathon/my-badges").catch(() => ({ data: null })),
      ]);
      setSummary(meR.data);
      setRewards(rewardsR.data.rewards || []);
      setBadges(badgesR.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/login?returnTo=/stepathon/rewards");
        return;
      }
      toast.error("Could not load your rewards");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const onRedeem = async (reward) => {
    const autoApplies = reward.reward_type !== "merch" && (reward.amount_dollars || 0) > 0;
    const ok = await brandedConfirm({
      title: `Redeem "${reward.name}"?`,
      description: autoApplies
        ? `This will deduct ${reward.points_cost.toLocaleString()} points from your balance and add a $${(reward.amount_dollars || 0).toFixed(2)} discount that auto-applies on your next GLPFlo refill order. Redemptions cannot be undone.`
        : `This will deduct ${reward.points_cost.toLocaleString()} points from your balance. Our team will follow up to confirm sizing/shipping. Redemptions cannot be undone.`,
      confirmText: "Redeem",
      cancelText: "Not yet",
      tone: "info",
    });
    if (!ok) return;
    setRedeeming(reward.id);
    try {
      const r = await API.post(`/stepathon/rewards/${reward.id}/redeem`);
      if (r.data.auto_apply) {
        toast.success(
          `Redeemed! $${(r.data.amount_dollars || 0).toFixed(2)} will auto-apply at your next checkout.`,
          { duration: 6000 }
        );
      } else if (r.data.coupon_code) {
        toast.success(`Redeemed! Code: ${r.data.coupon_code}`);
      } else {
        toast.success("Redeemed! Our team will email you to confirm details.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redeem failed");
    } finally {
      setRedeeming(null);
    }
  };

  if (loading || !summary) {
    return (
      <div className="min-h-screen bg-[#0a0612] text-white p-8" data-testid="rewards-loading">
        Loading your rewards…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1B0B3A] via-[#150728] to-[#0a0612] text-white">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <Link to="/stepathon" className="text-sm text-purple-200/80 hover:text-pink-300">
          ← all step-a-thons
        </Link>

        <div className="mt-4 flex items-center gap-3">
          <Gift className="w-8 h-8 text-pink-300" />
          <h1 data-testid="rewards-title" className="text-4xl sm:text-5xl font-black">
            Rewards
          </h1>
        </div>

        <div className="mt-8">
          <PointsHero summary={summary} />
        </div>

        {badges && (
          <section className="mt-10" data-testid="badges-section">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-2xl font-extrabold">Milestone Badges</h2>
              <span className="rounded-full bg-purple-500/20 text-purple-200 text-xs font-bold px-3 py-1">
                {badges.earned_count}/{badges.total_count}
              </span>
            </div>
            <p className="text-sm text-purple-100/80 mb-4">
              Lifetime steps: <b className="text-white">{Number(badges.total_steps || 0).toLocaleString()}</b>
              {badges.next_badge
                ? <> · {Number(badges.steps_until_next).toLocaleString()} more until <b>{badges.next_badge.emoji} {badges.next_badge.label}</b></>
                : <> · every badge earned 🎉</>}
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {badges.badges.map((b) => {
                const badgeUrl = `${process.env.REACT_APP_BACKEND_URL}/api/stepathon/badges/${b.tier}/share.png`;
                const shareBadge = async () => {
                  if (!b.earned) return;
                  try {
                    // Fetch as blob so we can share the actual image
                    // (Web Share API level 2 required for files).
                    const tabTok = sessionStorage.getItem("glpflo_tab_token") || "";
                    const res = await fetch(badgeUrl, {
                      credentials: "include",
                      headers: tabTok ? { "X-Tab-Token": tabTok } : {},
                    });
                    if (!res.ok) throw new Error("Couldn't build badge");
                    const blob = await res.blob();
                    const file = new File([blob], `glpflo-${b.tier}-badge.png`, { type: "image/png" });
                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                      await navigator.share({
                        files: [file],
                        title: `My ${b.label} badge`,
                        text: `Just unlocked the ${b.label} badge on GLPFlo — ${b.threshold >= 1_000_000 ? `${b.threshold / 1_000_000}M` : `${Math.round(b.threshold / 1000)}K`} lifetime steps! 🏆`,
                      });
                    } else {
                      // Fallback: open in a new tab so the user can save/screenshot.
                      const url = URL.createObjectURL(blob);
                      window.open(url, "_blank", "noopener");
                    }
                  } catch (e) {
                    console.warn("badge share failed", e);
                  }
                };
                return (
                  <button
                    type="button"
                    key={b.tier}
                    data-testid={`badge-${b.tier}`}
                    onClick={shareBadge}
                    disabled={!b.earned}
                    className={`aspect-square rounded-2xl border p-3 flex flex-col items-center justify-center text-center transition-all ${
                      b.earned
                        ? "border-purple-400 bg-purple-500/15 shadow-lg shadow-purple-500/20 cursor-pointer hover:scale-105"
                        : "border-white/10 bg-white/[0.03] opacity-60 cursor-default"
                    }`}
                  >
                    <div className={`text-4xl ${b.earned ? "" : "grayscale opacity-50"}`}>{b.emoji}</div>
                    <div className="mt-2 font-extrabold text-xs text-white truncate w-full">{b.label}</div>
                    <div className="text-[10px] text-purple-100/70 mt-0.5">
                      {b.threshold >= 1_000_000 ? `${b.threshold / 1_000_000}M` : `${Math.round(b.threshold / 1000)}K`} steps
                    </div>
                    {b.earned ? (
                      <div className="text-[10px] font-bold text-pink-200 mt-1">Tap to share</div>
                    ) : (
                      <div className="mt-2 h-1 w-4/5 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-400" style={{ width: `${b.progress_pct}%` }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <section className="mt-10" data-testid="squad-section">
          <SquadCard />
        </section>

        {leaderboard && leaderboard.top && leaderboard.top.length > 0 && (
          <section className="mt-10" data-testid="leaderboard-section">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <h2 className="text-2xl font-extrabold">Step-a-Thon Leaderboard</h2>
              <div
                data-testid="leaderboard-window-toggle"
                className="ml-auto inline-flex rounded-full bg-white/5 border border-purple-300/30 p-1 text-xs font-bold"
              >
                {[
                  { id: "week",  label: "Week" },
                  { id: "month", label: "Month" },
                  { id: "all",   label: "Lifetime" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`leaderboard-window-${opt.id}`}
                    onClick={() => setLbWindow(opt.id)}
                    className={`px-3 py-1 rounded-full transition-colors ${
                      lbWindow === opt.id
                        ? "bg-purple-500 text-white"
                        : "text-purple-100/70 hover:text-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-purple-300/30 bg-white/5 overflow-hidden">
              {leaderboard.top.map((row) => (
                <div
                  key={`${row.rank}-${row.first_name}`}
                  data-testid={`leaderboard-row-${row.rank}`}
                  className={`flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-b-0 ${row.is_me ? "bg-purple-500/25" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm ${
                      row.rank === 1 ? "bg-yellow-400 text-yellow-950" :
                      row.rank === 2 ? "bg-slate-300 text-slate-900" :
                      row.rank === 3 ? "bg-amber-600 text-amber-50" :
                      "bg-white/10 text-white"
                    }`}>
                      {row.rank}
                    </div>
                    <div className="font-bold text-white text-sm">
                      {row.first_name}
                      {row.is_me ? <span className="ml-1 text-[10px] uppercase tracking-widest text-pink-300">· You</span> : null}
                    </div>
                  </div>
                  <div className="tabular-nums font-mono text-sm text-purple-100/90">
                    {row.total_steps.toLocaleString()} <span className="text-[10px] opacity-70">steps</span>
                  </div>
                </div>
              ))}
              {leaderboard.me && !leaderboard.top.some(r => r.is_me) && leaderboard.me.rank && (
                <div className="flex items-center justify-between px-4 py-3 bg-purple-500/25 border-t border-white/10" data-testid="leaderboard-me-row">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center font-black text-sm">
                      #{leaderboard.me.rank}
                    </div>
                    <div className="font-bold text-white text-sm">
                      {leaderboard.me.first_name}
                      <span className="ml-1 text-[10px] uppercase tracking-widest text-pink-300">· You</span>
                    </div>
                  </div>
                  <div className="tabular-nums font-mono text-sm text-purple-100/90">
                    {leaderboard.me.total_steps.toLocaleString()} <span className="text-[10px] opacity-70">steps</span>
                  </div>
                </div>
              )}
            </div>
            <p className="text-[11px] text-purple-200/60 mt-2">
              Rankings are computed from lifetime step logs. Only first names are shown to keep it fun and private.
            </p>
          </section>
        )}


        <section className="mt-10">
          <h2 className="text-2xl font-extrabold mb-4">Redeemable rewards</h2>
          {rewards.length === 0 ? (
            <div data-testid="rewards-empty"
                 className="rounded-3xl border border-purple-300/30 bg-white/5 p-8 text-center text-purple-100/80">
              No rewards available right now. Keep walking — new ones drop every refill cycle.
            </div>
          ) : (
            <div data-testid="rewards-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rewards.map((r) => (
                <RewardCard
                  key={r.id}
                  reward={r}
                  available={summary.points_available}
                  onRedeem={onRedeem}
                />
              ))}
            </div>
          )}
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-extrabold mb-4">Your redemption history</h2>
          {summary.history.length === 0 ? (
            <div data-testid="history-empty"
                 className="rounded-3xl border border-purple-300/30 bg-white/5 p-6 text-sm text-purple-100/80">
              Nothing redeemed yet — your first reward is one walk away.
            </div>
          ) : (
            <div data-testid="history-list"
                 className="rounded-3xl border border-purple-300/30 bg-white/5 overflow-hidden">
              {summary.history.map((h) => (
                <div key={h.id}
                     className="flex items-center gap-4 px-5 py-4 border-b border-purple-200/10 last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{h.reward_name}</div>
                    <div className="text-xs text-purple-200/70">
                      {h.redeemed_at && new Date(h.redeemed_at).toLocaleString()}
                    </div>
                    {h.coupon_code && (
                      <div className="mt-1">
                        <CouponPill code={h.coupon_code} />
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-bold tabular-nums">
                      −{h.points_spent.toLocaleString()} pts
                    </div>
                    {h.status === "fulfilled" ? (
                      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-300">
                        <CheckCircle2 className="w-3 h-3" /> Fulfilled
                      </div>
                    ) : (
                      <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300">
                        {h.status}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
